// Supabase generated types — placeholder until `supabase gen types typescript` is run.
// Hand-written from supabase/migrations/0001_init.sql + 0006_i18n_jsonb.sql.
// Regenerate after schema changes.

/**
 * JSONB i18n map: { en: "…", es: "…", ar: "…", … }. The key is an ISO-639-1
 * language code; the value is the translated text. Missing languages fall
 * back to English (or the original text column when JSONB is empty).
 */
export type I18nMap = Record<string, string>;

/**
 * Resolve a translated string from an i18n map with a graceful fallback
 * chain: (requested lang) → en → original-text fallback.
 */
export function pickI18n(
  i18n: I18nMap | null | undefined,
  fallback: string | null | undefined,
  lang: string,
): string {
  if (i18n && typeof i18n === 'object') {
    const localized = i18n[lang];
    if (typeof localized === 'string' && localized.length > 0) return localized;
    const english = i18n.en;
    if (typeof english === 'string' && english.length > 0) return english;
  }
  return fallback ?? '';
}

export type Pathogen =
  | 'andes' | 'sin-nombre' | 'hantaan' | 'puumala' | 'seoul' | 'dobrava'
  | 'choclo' | 'laguna-negra' | 'bayou' | 'black-creek-canal'
  | 'tula' | 'thailand'
  | 'other' | 'unspecified';

export type LocationPrecision = 'exact' | 'approximate' | 'estimated';

export type Syndrome = 'HPS' | 'HFRS' | 'unspecified';
export type OutbreakStatus = 'active' | 'concluded' | 'historical';
export type Confidence = 'verified' | 'preliminary' | 'unverified';
export type IngestionMethod = 'manual' | 'auto' | 'auto-reviewed';
export type ReviewStatus = 'pending' | 'approved' | 'rejected';

export interface OutbreakRow {
  id: string;
  pathogen: Pathogen;
  syndrome: Syndrome;
  country_iso2: string;
  countries_affected: string[];
  // English copy plus a JSONB map of translations. Use pickI18n() to read.
  region: string | null;
  region_i18n: I18nMap;
  location_name: string;
  location_name_i18n: I18nMap;
  location_lat: number;
  location_lng: number;
  start_date: string;
  end_date: string | null;
  cases_confirmed: number;
  cases_suspected: number;
  cases_deaths: number;
  cases_last_updated: string;
  summary: string;
  summary_i18n: I18nMap;
  significance: string | null;
  significance_i18n: I18nMap;
  status: OutbreakStatus;
  location_precision: LocationPrecision;
  confidence: Confidence;
  ingestion_method: IngestionMethod;
  ingestion_last_updated: string;
  created_at: string;
  updated_at: string;
}

export interface SourceRow {
  url: string;
  title: string;
  title_i18n: I18nMap;
  publisher: string;
  publisher_i18n: I18nMap;
  tier: 1 | 2 | 3 | 4;
  accessed: string;
}

/**
 * One row in the outbreak's chronological story arc — added by the
 * /admin UI, the ingest pipeline, or the Perplexity closeout watcher.
 * Rendered as a timeline beneath each event's summary on the detail
 * panel. Headline is the single-line newspaper-style statement; details
 * is optional longer prose. delta carries structured field changes for
 * machine-readable audit (e.g. `{cases_confirmed: {from: 2, to: 5}}`).
 */
export type OutbreakEventType =
  | 'opened'
  | 'cases_updated'
  | 'lab_negative'
  | 'lab_positive'
  | 'death'
  | 'recovery'
  | 'status_changed'
  | 'closed'
  | 'sequenced'
  | 'note';

export interface OutbreakEvent {
  event_at: string;            // ISO timestamp (real-world time of the change)
  event_type: OutbreakEventType;
  headline: string;            // English headline
  headline_i18n: I18nMap;
  details: string | null;
  details_i18n: I18nMap;
  delta: Record<string, unknown>;
  source_url: string | null;
  source_publisher: string | null;
  source_tier: 1 | 2 | 3 | 4 | null;
}

export interface OutbreakWithSources extends OutbreakRow {
  sources: SourceRow[];
  events: OutbreakEvent[];
}

export interface PendingOutbreakRow {
  id: string;
  payload: unknown;
  submitted_by: string | null;
  submitted_at: string;
  submitter_ip: string | null;
  review_status: ReviewStatus;
  review_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  promoted_to: string | null;
}
